/**
 * Шаг «Рабочие места» визарда СОУТ (canon service-sout, p3-sout-workplaces §1/§3). Плашка
 * «Добавить рабочие места можно тремя способами» + кнопки «Из структуры компании» (модал —
 * `StructureModal`, Волна 5, §2)/«Добавить вручную» (страница — Волна 7, роут уже рабочий) +
 * счётчик «Добавлено: N рабочих мест» со склонением. Список: группировка по подразделениям со
 * сквозной нумерацией по заказу, «Без подразделения» — отдельная (оранжевая) группа, строка —
 * номер/должность/бейдж источника («из структуры»/«вручную», D2.1 решение B)/ФИО/СНИЛС/статус
 * «дозаполнить» (тёмно-оранжевая рамка + светлый оранжевый фон — НЕ красный) + кнопки
 * «Открыть»/«Копия»/«Убрать». Карточка места («Открыть») — отдельная страница
 * (`/orders/:id/sout/workplaces/:lineId`, §4); эта страница восстанавливает позицию в списке
 * по `anchorLineId` (якорь из URL, читает мастер при монтировании).
 */
import { useEffect, useRef, useState } from 'react'
import { Copy, FileSpreadsheet, Plus, Trash2, Users } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { soutApi, type SoutLine } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { computeLineMissing, isLineValid, workplacesCountLabel } from '@/lib/sout'
import {
  SOUT_WORKPLACES_INTRO_ITEMS,
  SOUT_WORKPLACES_INTRO_TITLE,
} from './sout-copy'
import { StructureModal } from './structure-modal'
import { ORDER_IMPORT_BUTTON } from '@/orders/order-import-dialog'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'

interface IndexedLine {
  line: SoutLine
  index: number
}

/** Группировка строк по подразделению с сохранением сквозной нумерации «№N» по заказу (§3). */
function groupByDepartment(lines: SoutLine[]): Array<{ department: string; items: IndexedLine[] }> {
  const order: string[] = []
  const map = new Map<string, IndexedLine[]>()
  lines.forEach((line, i) => {
    const dep = line.conditions.departmentName.trim() || 'Без подразделения'
    if (!map.has(dep)) {
      map.set(dep, [])
      order.push(dep)
    }
    map.get(dep)!.push({ line, index: i + 1 })
  })
  return order.map((department) => ({ department, items: map.get(department)! }))
}

/** Заголовок группы подразделения (§3): наименование + количество со склонением; «Без
 *  подразделения» — оранжевое оформление (аналог `.wp-grp-h.nodept` макета), обычная группа —
 *  акцентное (аналог `.wp-grp-h`). */
function DepartmentGroupHeader({ department, count }: { department: string; count: number }) {
  const noDept = department === 'Без подразделения'
  return (
    <div
      className={cn(
        'mb-2 flex items-center gap-2 rounded-8 border-l-[3px] px-3 py-2 text-13 font-semibold',
        noDept ? 'border-amber bg-amber-bg text-amber' : 'border-accent bg-accent-bg text-accent-txt',
      )}
    >
      <span>{department}</span>
      <span className="ml-auto text-12 font-normal text-txt-secondary">{workplacesCountLabel(count)}</span>
    </div>
  )
}

function LineRow({
  item,
  readOnly,
  disabled,
  onOpen,
  onCopy,
  onDelete,
}: {
  item: IndexedLine
  readOnly: boolean
  /** Идёт мутация списка (copy/remove) — действия над строкой заблокированы. */
  disabled: boolean
  onOpen: () => void
  onCopy: () => void
  onDelete: () => void
}) {
  const { line, index } = item
  const valid = isLineValid(line)
  const missingCount = computeLineMissing(line).length
  const [confirming, setConfirming] = useState(false)
  const meta = [line.person.fullName, line.person.snils ? `СНИЛС ${line.person.snils}` : '']
    .filter((s) => s.trim())
    .join(' · ')

  return (
    <div
      id={`wp-${line.id}`}
      className={cn(
        'flex flex-wrap items-center gap-3.5 rounded-10 border p-3',
        valid || readOnly ? 'border-line bg-white' : 'border-amber bg-amber-bg',
      )}
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-bg text-13 font-semibold text-accent-txt">
        {index}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-14 font-semibold text-txt">
          <span>{line.conditions.position || 'Должность не указана'}</span>
          {/* Бейдж источника строки (D2.1, решение B): «из структуры» (синий) — bulk-модал
              §2, «из файла» (зелёный) — загрузка анкеты (order-import-legacy-forms), «вручную»
              (серый) — любой другой путь создания. Легаси-строки без поля lineSource — бейдж
              не показываем (не выдумываем источник задним числом). */}
          {line.lineSource === 'structure' ? <Badge variant="blue">из структуры</Badge> : null}
          {line.lineSource === 'import' ? <Badge variant="green">из файла</Badge> : null}
          {line.lineSource === 'manual' ? <Badge variant="gray">вручную</Badge> : null}
          {!valid && !readOnly ? (
            <Badge status="warning">дозаполнить{missingCount ? ` (${missingCount})` : ''}</Badge>
          ) : null}
        </div>
        <p className="mt-0.5 text-12 text-txt-secondary">
          {meta || 'ФИО и СНИЛС появятся после заполнения карточки'}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" onClick={onOpen}>
          Открыть
        </Button>
        {!readOnly ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={onCopy}
            leftIcon={<Copy className="size-4 stroke-current" aria-hidden />}
          >
            Копия
          </Button>
        ) : null}
        {!readOnly && !confirming ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={disabled}
            onClick={() => setConfirming(true)}
            leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
          >
            Убрать
          </Button>
        ) : null}
        {!readOnly && confirming ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
              Отмена
            </Button>
            <Button size="sm" disabled={disabled} onClick={onDelete}>
              Точно убрать
            </Button>
          </>
        ) : null}
      </div>
    </div>
  )
}

export interface WorkplacesStepProps {
  orderId: string
  lines: SoutLine[]
  readOnly: boolean
  /** Заменить весь список (после копирования/удаления — уже сохранено на сервере). */
  onLinesChange: (lines: SoutLine[]) => void
  /** id строки, к которой нужно прокрутить список при возврате со страницы рабочего места
   *  (§3.1/§4 — позиция пользователя в списке сохраняется). */
  anchorLineId?: string
  /**
   * Открыть загрузку «Анкеты и формы СОУТ» (order-import-legacy-forms). Диалог живёт в мастере:
   * загрузка меняет и анкету (комиссия, ответственный), которую мастер держит в своём состоянии.
   */
  onImportFile?: () => void
}

export function WorkplacesStep({
  orderId,
  lines,
  readOnly,
  onLinesChange,
  anchorLineId,
  onImportFile,
}: WorkplacesStepProps) {
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Модал «Рабочие места из структуры компании» (§2, D2, Волна 5) — bulk-добавление
  // сотрудников и вакансий из структуры (structure-modal.tsx).
  const [structureModalOpen, setStructureModalOpen] = useState(false)
  const scrolledRef = useRef(false)

  useEffect(() => {
    if (!anchorLineId || scrolledRef.current) return
    const el = document.getElementById(`wp-${anchorLineId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      scrolledRef.current = true
    }
  }, [anchorLineId, lines])

  // Opus-ревью Волны 4 (MAJOR-2): ПЕРЕД переходом на отдельную страницу — заменяем query текущей
  // записи `/orders/:id/sout` на `?step=3&line=<id>` (шаг «Рабочие места» + строка-якорь).
  // Без этого браузерный «Назад» со страницы места возвращал бы на запись БЕЗ query (историческая
  // запись, созданная до открытия «Рабочие места»), и мастер стартовал бы заново с шага 0.
  function openLine(lineId: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('step', '3')
        next.set('line', lineId)
        return next
      },
      { replace: true },
    )
    navigate(`/orders/${orderId}/sout/workplaces/${lineId}`)
  }

  function addManually() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('step', '3')
        next.delete('line')
        return next
      },
      { replace: true },
    )
    navigate(`/orders/${orderId}/sout/workplaces/new`)
  }

  async function copy(line: SoutLine) {
    setError(null)
    setBusy(true)
    try {
      const created = await soutApi.addLine(orderId, { copyFromLineId: line.id })
      onLinesChange([...lines, created])
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось скопировать место'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(line: SoutLine) {
    setError(null)
    setBusy(true)
    try {
      await soutApi.deleteLine(orderId, line.id)
      onLinesChange(lines.filter((l) => l.id !== line.id))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось убрать место'))
    } finally {
      setBusy(false)
    }
  }

  const groups = groupByDepartment(lines)

  return (
    <div className="flex flex-col gap-4">
      {!readOnly ? (
        <Alert
          variant="info"
          icon={<Users className="size-5 stroke-current" aria-hidden />}
          title={SOUT_WORKPLACES_INTRO_TITLE}
        >
          {/* Нумерованный список способов добавления (centrika-docs,
              component.create-sout-order.workplaces, MR !9). */}
          <ol className="list-decimal pl-5">
            {SOUT_WORKPLACES_INTRO_ITEMS.map((item) => (
              <li key={item.name}>
                <strong className="font-medium">{item.name}</strong> — {item.text}
              </li>
            ))}
          </ol>
        </Alert>
      ) : null}

      {!readOnly ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => setStructureModalOpen(true)}
            leftIcon={<Users className="size-4 stroke-current" aria-hidden />}
          >
            Из структуры компании
          </Button>
          <Button
            variant="secondary"
            onClick={addManually}
            leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
          >
            Добавить вручную
          </Button>
          {onImportFile ? (
            <Button
              variant="secondary"
              onClick={onImportFile}
              leftIcon={<FileSpreadsheet className="size-4 stroke-current" aria-hidden />}
            >
              {ORDER_IMPORT_BUTTON}
            </Button>
          ) : null}
          <span className="text-txt-secondary">Добавлено: {workplacesCountLabel(lines.length)}</span>
        </div>
      ) : (
        <p className="text-txt-secondary">Добавлено: {workplacesCountLabel(lines.length)}</p>
      )}

      {structureModalOpen ? (
        <StructureModal
          orderId={orderId}
          lines={lines}
          onClose={() => setStructureModalOpen(false)}
          onAdded={(added) => {
            if (added.length > 0) onLinesChange([...lines, ...added])
          }}
        />
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}

      {lines.length === 0 ? (
        <Panel>
          <EmptyState
            icon={Plus}
            title="Пока не добавлено ни одного рабочего места…"
            description={
              readOnly ? undefined : 'Выберите сотрудников «Из структуры компании» или добавьте вручную'
            }
          />
        </Panel>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => (
            <div key={g.department}>
              <DepartmentGroupHeader department={g.department} count={g.items.length} />
              <div className="flex flex-col gap-2">
                {g.items.map((item) => (
                  <LineRow
                    key={item.line.id}
                    item={item}
                    readOnly={readOnly}
                    disabled={busy}
                    onOpen={() => openLine(item.line.id)}
                    onCopy={() => void copy(item.line)}
                    onDelete={() => void remove(item.line)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  )
}
